import type { SweepstakesAnswerKey } from "@/utils/constants";

/** PostHog multivariate flag that assigns each visitor one popup variant. */
export const POPUP_OFFER_FLAG_KEY = "popup-offer-test";

export const POPUP_VARIANTS = ["A", "B", "C", "D", "E"] as const;
export type PopupVariant = (typeof POPUP_VARIANTS)[number];

/** Unenrolled visitors (flag missing, blocked, or timed out) see today's popup. */
export const DEFAULT_POPUP_VARIANT: PopupVariant = "A";

export const isPopupVariant = (value: unknown): value is PopupVariant =>
  typeof value === "string" &&
  (POPUP_VARIANTS as readonly string[]).includes(value);

export const POPUP_GIFTS = [
  "bag_pass",
  "matic_care",
  "dock",
  "pet_bundle",
] as const;
export type PopupGift = (typeof POPUP_GIFTS)[number];

/** Gifts offered on their own, rather than picked by the quiz. */
export type StandaloneGift = Extract<PopupGift, "bag_pass" | "matic_care">;

export type PopupOffer =
  | { kind: "sweepstakes" }
  | { kind: "gift"; gift: StandaloneGift }
  | { kind: "quiz" };

export type PopupLayout = "card" | "fullscreen";

type PopupVariantConfig = {
  /** Minimum time on site before the popup may open, once the visitor scrolls. */
  delayMs: number;
  layout: PopupLayout;
  offer: PopupOffer;
};

export const POPUP_VARIANT_CONFIG: Record<PopupVariant, PopupVariantConfig> = {
  A: { delayMs: 0, layout: "card", offer: { kind: "sweepstakes" } },
  B: { delayMs: 10_000, layout: "fullscreen", offer: { kind: "sweepstakes" } },
  C: {
    delayMs: 20_000,
    layout: "fullscreen",
    offer: { kind: "gift", gift: "bag_pass" },
  },
  D: {
    delayMs: 30_000,
    layout: "card",
    offer: { kind: "gift", gift: "matic_care" },
  },
  E: { delayMs: 20_000, layout: "fullscreen", offer: { kind: "quiz" } },
};

/**
 * What each gift is and the Shopify code that frees it with a Matic. The codes
 * are referenced by the Klaviyo gift emails, so renaming one means updating its
 * email too.
 */
export const POPUP_GIFT_DETAILS: Record<
  PopupGift,
  { name: string; code: string; valueUsd: number }
> = {
  bag_pass: { name: "Annual Bag Pass", code: "HEPACLEAN", valueUsd: 120 },
  matic_care: { name: "Matic Care", code: "COVERED", valueUsd: 180 },
  dock: { name: "Extra Dock", code: "UPSTAIRS", valueUsd: 98 },
  pet_bundle: {
    name: "Pet Brush Roll + Annual Bag Pass",
    code: "FURBEGONE",
    valueUsd: 155,
  },
};

const QUIZ_GIFT_BY_ANSWER: Record<SweepstakesAnswerKey, PopupGift> = {
  endless_pet_hair: "pet_bundle",
  less_kids: "matic_care",
  multiple_floors: "dock",
  hate_mopping: "bag_pass",
  hate_vacuuming: "bag_pass",
};

/** The gift a signup earns, or null when the offer is the $500 draw. */
export const giftForOffer = (
  offer: PopupOffer,
  answer: SweepstakesAnswerKey | null,
): PopupGift | null => {
  switch (offer.kind) {
    case "sweepstakes":
      return null;
    case "gift":
      return offer.gift;
    case "quiz":
      return answer ? QUIZ_GIFT_BY_ANSWER[answer] : "bag_pass";
  }
};
